import {Component, Input, OnInit, OnDestroy} from '@angular/core';
import {SurveyService} from '../../../services/surveys.service';
import {Router} from '@angular/router';
import {PageEvent} from '@angular/material/paginator';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-survey-questions-dashboard',
    templateUrl: './survey-questions-dashboard.component.html',
    styleUrls: ['./survey-questions-dashboard.component.scss'],
    standalone: false
})
export class SurveyQuestionsDashboardComponent implements OnInit, OnDestroy {
  @Input() survey: any;

  filters: any = {
    custom: {},
    search: '',
    typologies: [],
    required: '',
    final_decision: ''
  };

  typologyOptions = [
    { value: 'CONSENSUS', label: 'service.survey-service.question-typologies.consensus.title' },
    { value: 'QUANTITATIVE', label: 'service.survey-service.question-typologies.quantitative.title' },
    { value: 'MULTIPLE_CHOICE', label: 'service.survey-service.question-typologies.multiple-choice.title' },
    { value: 'CHECKBOX', label: 'service.survey-service.question-typologies.checkbox.title' },
    { value: 'GRID', label: 'service.survey-service.question-typologies.grid.title' },
    { value: 'OPENED', label: 'service.survey-service.question-typologies.opened.title' },
    { value: 'CLASSIFICATION', label: 'service.survey-service.question-typologies.classification.title' }
  ];

  decisionOptions = [
    { value: 'accepted', label: 'service.survey-service.question-final-decision-statuses.accepted.title' },
    { value: 'reproposed', label: 'service.survey-service.question-final-decision-statuses.reproposed.title' },
    { value: 'revised', label: 'service.survey-service.question-final-decision-statuses.revised.title' },
    { value: 'revised_split', label: 'service.survey-service.question-final-decision-statuses.revised_split.title' },
    { value: 'rejected', label: 'service.survey-service.question-final-decision-statuses.rejected.title' },
    { value: 'pending', label: 'components.decision-table.show-decision-table.not-evaluated-yet' }
  ];

  private searchSubject = new Subject<string>();
  private searchSubscription!: Subscription;

  loading = false;
  surveyQuestionHistories: any = null;
  currentPageIndex: number = 0;
  pageSize: number = 1;
  totalSurveyQuestionHistories = 0;

  filteredHistories: any[] = [];

  hasActiveCustomFilters(): boolean {
    const custom = this.filters.custom;
    if (!custom) return false;
    return Object.keys(custom).some(key => {
      const filterConfig = custom[key];
      if (filterConfig === undefined || filterConfig === null) return false;
      if (Array.isArray(filterConfig)) return filterConfig.length > 0;
      if (typeof filterConfig === 'object') {
        return (filterConfig.min !== null && filterConfig.min !== undefined && filterConfig.min !== '') ||
               (filterConfig.max !== null && filterConfig.max !== undefined && filterConfig.max !== '');
      }
      return filterConfig !== 'ALL';
    });
  }

  onFilterChange() {
    this.applyFilters();
  }

  applyFilters() {
    if (!this.surveyQuestionHistories) {
      this.filteredHistories = [];
      return;
    }
    const filtersActive = this.hasActiveCustomFilters();
    this.filteredHistories = this.surveyQuestionHistories.map(history => {
      const clonedHistory = JSON.parse(JSON.stringify(history));
      if (clonedHistory.questions) {
        clonedHistory.questions = clonedHistory.questions.map(q => {
          if (q.responses) {
            // Map raw relations to the answer field for non-trivial typologies
            q.responses.forEach((r: any) => {
              if (q.typology === 'MULTIPLE_CHOICE') {
                r.answer = r.choice_answers?.[0]?.choice?.title ?? 'N/A';
              } else if (q.typology === 'CHECKBOX') {
                r.answer = r.choice_answers?.map((ca: any) => ca.choice?.title).filter(Boolean) || [];
              } else if (q.typology === 'GRID') {
                r.answer = r.grid_question_answers?.map((ga: any) => ({
                  row: ga.grid_question_row_id,
                  column: ga.grid_question_column_id
                })) || [];
              }
            });

            if (filtersActive) {
              q.responses = q.responses.filter(r => this.matchesCustomFilters(r));
            }
          }
          if (filtersActive) {
            this.recalculateQuestionMetrics(q);
          }
          return q;
        });
      }
      return clonedHistory;
    });
  }

  recalculateQuestionMetrics(q: any) {
    const typology = q.typology;
    const responses = q.responses || [];

    // Initialize results if null
    if (!q.results) {
      q.results = {};
    }

    if (q.choices) {
      q.choices = q.choices.map(c => {
        let count = 0;
        if (typology === 'CHECKBOX') {
          count = responses.filter(r => Array.isArray(r.answer) && r.answer.includes(c.title)).length;
        } else {
          count = responses.filter(r => r.answer === c.title).length;
        }
        return { ...c, answers_count: count, responses_count: count };
      });
    }

    if (typology === 'CONSENSUS') {
      q.agreeCount = responses.filter(r => r.answer === 'AGREE').length;
      q.disagreeCount = responses.filter(r => r.answer === 'DISAGREE').length;
      q.abstainedCount = responses.filter(r => r.status === 'ABSTAINED' || r.answer === 'ABSTAINED').length;

      // Recalculate certainty and contradiction degree
      const agreeResponses = responses.filter(r => r.answer === 'AGREE');
      const disagreeResponses = responses.filter(r => r.answer === 'DISAGREE');
      const totalCount = responses.length;

      if (totalCount === 0) {
        q.results.certainty_degree = 0;
        q.results.contradiction_degree = 0;
        q.results.a_ratio = 0;
      } else {
        const favourWeight = agreeResponses.reduce((sum, r) => sum + ((r.confidence ?? r.customs?.confidence ?? r.score ?? r.customs?.score ?? 100) / 100), 0);
        const contraryWeight = disagreeResponses.reduce((sum, r) => sum + ((r.confidence ?? r.customs?.confidence ?? r.score ?? r.customs?.score ?? 100) / 100), 0);

        const favourRatio = favourWeight / totalCount;
        const contraryRatio = contraryWeight / totalCount;

        q.results.certainty_degree = Math.abs(favourRatio - contraryRatio) * 100;
        q.results.contradiction_degree = Math.min(favourRatio, contraryRatio) * 2 * 100;
        q.results.a_ratio = (agreeResponses.length / totalCount) * 100;
      }
    }

    if (typology === 'QUANTITATIVE') {
      const newIncidence = {};
      responses.forEach(r => {
        const val = r.answer;
        if (val !== undefined && val !== null && val !== 'N/A') {
          const key = Number(val).toFixed(2);
          newIncidence[key] = (newIncidence[key] || 0) + 1;
        }
      });
      q.incidence = newIncidence;

      const numericAnswers = responses
        .map(r => Number(r.answer))
        .filter(n => !isNaN(n));

      if (numericAnswers.length === 0) {
        q.results.mean = 0;
        q.results.median = 0;
        q.results.min = 0;
        q.results.max = 0;
        q.results.q1 = 0;
        q.results.q3 = 0;
        q.results.geometric_mean = 0;
        q.results.variance = 0;
      } else {
        numericAnswers.sort((a, b) => a - b);
        const sum = numericAnswers.reduce((a, b) => a + b, 0);
        const mean = sum / numericAnswers.length;

        const mid = Math.floor(numericAnswers.length / 2);
        const median = numericAnswers.length % 2 !== 0 ? numericAnswers[mid] : (numericAnswers[mid - 1] + numericAnswers[mid]) / 2;
        const min = numericAnswers[0];
        const max = numericAnswers[numericAnswers.length - 1];

        const getPercentile = (arr, p) => {
          const index = (arr.length - 1) * p;
          const lower = Math.floor(index);
          const upper = Math.ceil(index);
          const weight = index - lower;
          return arr[lower] * (1 - weight) + arr[upper] * weight;
        };
        const q1 = getPercentile(numericAnswers, 0.25);
        const q3 = getPercentile(numericAnswers, 0.75);

        const geomMean = Math.exp(numericAnswers.reduce((a, b) => a + Math.log(b > 0 ? b : 1), 0) / numericAnswers.length);
        const variance = numericAnswers.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / numericAnswers.length;

        const newResults = {
          ...q.results,
          mean,
          median,
          min,
          max,
          q1,
          q3,
          geometric_mean: geomMean,
          variance
        };

        q.results = newResults;
        q.quantitative_results = newResults;
      }
    }

    if (typology === 'GRID') {
      if (q.grid_rows && q.grid_columns) {
        q.grid_rows = q.grid_rows.map(row => {
          const newAnswersCount = {};
          q.grid_columns.forEach(col => {
            const count = responses.filter(r => {
              return Array.isArray(r.answer) && r.answer.some(a =>
                (a.row == row.id || a.grid_question_row_id == row.id) &&
                (a.column == col.id || a.grid_question_column_id == col.id)
              );
            }).length;
            newAnswersCount[col.id] = count;
          });
          return { ...row, answers_count: newAnswersCount };
        });
      }
    }

    if (typology === 'CLASSIFICATION') {
      if (q.grid_rows && q.grid_columns) {
        q.grid_rows = q.grid_rows.map(row => {
          const newAnswersCount = {};
          q.grid_columns.forEach(col => {
            const values: number[] = [];
            responses.forEach(r => {
              if (r.status !== 'ABSTAINED') {
                const ans = r.raw_answer ?? r.answer;
                if (ans && typeof ans === 'object' && ans[row.id] !== undefined && ans[row.id][col.id] !== undefined) {
                  const val = parseFloat(ans[row.id][col.id]);
                  if (!isNaN(val)) {
                    values.push(val);
                  }
                }
              }
            });
            const avg = values.length > 0 ? values.reduce((acc, b) => acc + b, 0) / values.length : 0;
            newAnswersCount[col.id] = Math.round(avg * 100) / 100;
          });
          return { ...row, answers_count: newAnswersCount };
        });
      }
    }
  }

  matchesCustomFilters(response: any): boolean {
    const custom = this.filters.custom;
    if (!custom || Object.keys(custom).length === 0) return true;

    const email = response.survey_answer?.email;
    if (!email) return false;

    const member = (this.survey?.members || []).find(m =>
      m.email?.toLowerCase().trim() === email.toLowerCase().trim()
    );
    if (!member) return false;

    const customFields = this.survey?.team_member_custom_fields || [];
    for (const fieldId of Object.keys(custom)) {
      const field = customFields.find(f => f.id == fieldId);
      if (!field) continue;

      const filterConfig = custom[fieldId];
      const val = member.customs?.team_member_custom_fields?.[field.id];

      if (field.type === 'boolean') {
        if (filterConfig !== 'ALL') {
          const expected = filterConfig === 'YES';
          if (val !== expected) return false;
        }
      } else if (field.type === 'number' || field.type === 'date') {
        const { min, max } = filterConfig;
        const isMinActive = min !== null && min !== undefined && min !== '';
        const isMaxActive = max !== null && max !== undefined && max !== '';

        let compareVal = val;
        let compareMin = min;
        let compareMax = max;

        if (field.type === 'date') {
          compareVal = val ? new Date(val).getTime() : undefined;
          compareMin = min ? new Date(min).getTime() : undefined;
          compareMax = max ? new Date(max).getTime() : undefined;

          if (compareVal) compareVal = new Date(new Date(compareVal).setHours(0, 0, 0, 0)).getTime();
          if (compareMin) compareMin = new Date(new Date(compareMin).setHours(0, 0, 0, 0)).getTime();
          if (compareMax) compareMax = new Date(new Date(compareMax).setHours(23, 59, 59, 999)).getTime();
        }

        if (isMinActive && (compareVal === undefined || compareVal === null || compareVal < compareMin)) return false;
        if (isMaxActive && (compareVal === undefined || compareVal === null || compareVal > compareMax)) return false;
      }
      else if (field.type === 'text') {
        if (filterConfig && filterConfig.length > 0 && !filterConfig.includes(val)) return false;
      }
    }
    return true;
  }

  constructor(
    private surveyService: SurveyService,
    private router: Router,
    public translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.init();
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(searchText => {
      this.filters.search = searchText;
      this.currentPageIndex = 0;
      this.loadSurveyQuestionsHistories(1, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  init() {
    this.loadSurveyQuestionsHistories(this.currentPageIndex + 1, this.pageSize);
  }

  loadSurveyQuestionsHistories(page: number, perPage: number) {
    this.loading = true;
    this.surveyService.getSurveyQuestionsHistoriesBySurveyID(this.survey?.id, page, perPage, this.filters, (result) => {
      this.surveyQuestionHistories = result.data;
      this.totalSurveyQuestionHistories = result.total;
      this.applyFilters();
      this.loading = false;
    }, (err) => {
      this.loading = false;
    });
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  clearSearch() {
    this.filters.search = '';
    this.searchSubject.next('');
  }

  onTypologyChange() {
    this.currentPageIndex = 0;
    this.loadSurveyQuestionsHistories(1, this.pageSize);
  }

  onRequiredChange() {
    this.currentPageIndex = 0;
    this.loadSurveyQuestionsHistories(1, this.pageSize);
  }

  onDecisionChange() {
    this.currentPageIndex = 0;
    this.loadSurveyQuestionsHistories(1, this.pageSize);
  }

  getSelectedTypologiesLabel(): string {
    if (!this.filters.typologies || this.filters.typologies.length === 0) {
      return this.translateService.instant('generic.all') || 'Tutte';
    }
    if (this.filters.typologies.length === 1) {
      const selected = this.typologyOptions.find(o => o.value === this.filters.typologies[0]);
      return selected ? this.translateService.instant(selected.label) : '';
    }
    return `${this.filters.typologies.length} selezionate`;
  }

  onPageChange(event: PageEvent) {
    this.currentPageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadSurveyQuestionsHistories(this.currentPageIndex + 1, this.pageSize);
  }
}
